import React from "react"
import PropTypes from "prop-types"
import { Media, Div, Para, Img, PlayButton, CloudButton, Text, Skills, Skill, Avatar } from "./styles"
import { H3, P } from "../typography"
import Banner from "./banner"
import Grid from "../grid"
import Moment from "../moment"
import Card from "../card"

const ParticipantTile = ({
  id,
  avatar_exists,
  avatar_path,
  obfuscated,
  video_path,
  banner,
  programme_name,
  age,
  gender,
  country_code,
  display_name,
  first_name,
  last_name,
  availability_from,
  availability_until,
  student_status,
  has_visa,
  skills,
  skill_count,
  buttons,
  clickHandler,
  playVideoHandler,
  downloadPdfHandler,
  placed_section,
  arrival_date,
  starts_on,
  ends_on,
  placement_position,
  status,
  inline_avatar,
  showSkillRating
}) => {
  const remainingSkillCount = skill_count - skills.length
  let summary = `${programme_name} \u00b7 ${age} \u00b7 ${gender} \u00b7 ${country_code}`
  if (has_visa && !placed_section) {
    summary += ` \u00b7 Visa \u2714`
  }

  const hideAvatar = !avatar_exists || !!obfuscated

  const card = (
    <Card variant="white" paddingless>
      {!!inline_avatar && avatar_exists && <Avatar src={avatar_path} alt={display_name} />}

      <Grid gap="0.5rem">
        {inline_avatar != true && (
          <Media>
            {banner !== undefined && <Banner {...banner} />}
            {hideAvatar ? (
              <Div>
                <Para>{`${first_name[0]}${last_name[0]}`}</Para>
              </Div>
            ) : (
              <Img src={avatar_path} alt={display_name} />
            )}
            {playVideoHandler !== undefined && (
              <PlayButton
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={playVideoHandler}
              >
                <path d="M16 10.0001L7 15.1963L7 4.80397L16 10.0001Z" fill="white" />
                <circle cx="10" cy="10" r="9" stroke="white" stroke-width="2" />
              </PlayButton>
            )}
            {downloadPdfHandler !== undefined && (
              <CloudButton
                width="24"
                height="16"
                viewBox="0 0 24 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={downloadPdfHandler}
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M19.35 6.04C18.67 2.59 15.64 0 12 0C9.11 0 6.6 1.64 5.35 4.04C2.34 4.36 0 6.91 0 10C0 13.31 2.69 16 6 16H19C21.76 16 24 13.76 24 11C24 8.36 21.95 6.22 19.35 6.04ZM19 14H6C3.79 14 2 12.21 2 10C2 7.95 3.53 6.24 5.56 6.03L6.63 5.92L7.13 4.97C8.08 3.14 9.94 2 12 2C14.62 2 16.88 3.86 17.39 6.43L17.69 7.93L19.22 8.04C20.78 8.14 22 9.45 22 11C22 12.65 20.65 14 19 14ZM10.55 6H13.45V9H16L12 13L8 9H10.55V6Z"
                  fill="white"
                />
              </CloudButton>
            )}
          </Media>
        )}

        <Text>
          <Grid gap="0.75rem">
            <Grid gap="0.25rem">
              <P marginless variant="primary" size="small">
                {summary}
              </P>
              <H3 marginless variant="secondary">
                {display_name}
              </H3>

              {status !== "did_not_start" && (
                <>
                  {placed_section === true ? (
                    <>
                      <P marginless variant="primary" bold={true}>
                        {placement_position}
                      </P>
                      <P marginless variant="secondary">
                        Arriving <Moment value={arrival_date} format="DD MMM YYYY" />
                      </P>
                      <P marginless variant="secondary">
                        <Moment value={starts_on} format="DD MMM YYYY" /> -{" "}
                        <Moment value={ends_on} format="DD MMM YYYY" />
                      </P>
                    </>
                  ) : (
                    <>
                      <P marginless variant="secondary">
                        <Moment value={availability_from} format="DD MMM YYYY" /> -{" "}
                        <Moment value={availability_until} format="DD MMM YYYY" />
                      </P>
                      {student_status && (
                        <P marginless variant="secondary" size="small">
                          {student_status}
                        </P>
                      )}
                    </>
                  )}
                </>
              )}
            </Grid>
            {!placed_section && (
              <Grid gap="0.25rem">
                <P marginless variant="primary" size="small">
                  Skills
                </P>
                <Skills>
                  {skills.map((skill) => (
                    <Skill key={`review_${id}_skill_${skill.name}`}>
                      {skill.name}{" "}
                      {showSkillRating && skill.star_rating > 0 && [...Array(skill.star_rating)].map(() => "★")}
                      {showSkillRating && 4 - skill.star_rating > 0 && [...Array(4 - skill.star_rating)].map(() => "☆")}
                    </Skill>
                  ))}
                  {remainingSkillCount > 0 && (
                    <Skill key={`review_${id}_skills_remaining`}>{`+ ${remainingSkillCount} more`}</Skill>
                  )}
                </Skills>
              </Grid>
            )}

            {buttons !== undefined && <Grid gap="0.5rem">{buttons}</Grid>}
          </Grid>
        </Text>
      </Grid>
    </Card>
  )

  if (clickHandler === undefined) return card
  else
    return (
      <div onClick={clickHandler} style={{ cursor: "pointer" }}>
        {card}
      </div>
    )
}

ParticipantTile.propTypes = {
  id: PropTypes.number.isRequired,
  avatarExists: PropTypes.bool,
  avatar_path: PropTypes.string.isRequired,
  obfuscated: PropTypes.bool,
  video_path: PropTypes.string,
  programme_name: PropTypes.string.isRequired,
  age: PropTypes.string.isRequired,
  gender: PropTypes.string.isRequired,
  country_code: PropTypes.string.isRequired,
  display_name: PropTypes.string.isRequired,
  first_name: PropTypes.string.isRequired,
  last_name: PropTypes.string.isRequired,
  availability_from: PropTypes.string.isRequired,
  availability_until: PropTypes.string.isRequired,
  skills: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      star_rating: PropTypes.number.isRequired
    })
  ).isRequired,
  skill_count: PropTypes.number.isRequired,
  banner: PropTypes.shape({
    variant: PropTypes.oneOf(["default", "danger", "warning", "success"]),
    text: PropTypes.string
  }),
  buttons: PropTypes.node,
  clickHandler: PropTypes.func,
  playVideoHandler: PropTypes.func,
  downloadPdfHandler: PropTypes.func,
  showSkillRating: PropTypes.bool
}

ParticipantTile.defaultProps = {
  skills: [],
  banner: undefined,
  buttons: undefined,
  clickHandler: undefined,
  playVideoHandler: undefined,
  downloadPdfHandler: undefined,
  avatarExists: true,
  obfuscated: false,
  showSkillRating: true
}

export default ParticipantTile
